import type { EvidenceAnchor, EvidenceRecord } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Download, FileDown, FileText, Landmark, ScrollText } from "lucide-react";
import { type MouseEvent, useMemo } from "react";
import { toast } from "sonner";
import { Hash } from "@/components/domain/code";
import { AgentLink, PageHeader, TimeAgo } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { Label, Select } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/misc";
import { Tooltip } from "@/components/ui/tooltip";
import { ChainExplainer } from "@/features/evidence/chain-explainer";
import { EVIDENCE_KIND_META, EVIDENCE_KINDS, type EvidenceKind, READINESS_FRAMEWORKS } from "@/features/evidence/constants";
import { VerifyChainCard } from "@/features/evidence/verify-card";
import { q } from "@/lib/api";
import { downloadText } from "@/lib/download";
import { fmtDate, fmtInt } from "@/lib/format";

const WINDOWS = [100, 250, 500] as const;
type Window = (typeof WINDOWS)[number];

interface EvidenceSearch {
  kind?: EvidenceKind;
  window?: Window;
}

export const Route = createFileRoute("/_app/evidence")({
  validateSearch: (s: Record<string, unknown>): EvidenceSearch => ({
    kind: typeof s.kind === "string" && (EVIDENCE_KINDS as string[]).includes(s.kind) ? (s.kind as EvidenceKind) : undefined,
    window: WINDOWS.includes(Number(s.window) as Window) && Number(s.window) !== 100 ? (Number(s.window) as Window) : undefined,
  }),
  component: EvidencePage,
});

/** The loaded ledger window plus anchors, oldest record first, with how to verify it offline. */
function exportBundle(records: EvidenceRecord[], anchors: EvidenceAnchor[]) {
  const ordered = [...records].sort((a, b) => a.seq - b.seq);
  const bundle = {
    format: "traces.insure/evidence-bundle@1",
    exportedAt: new Date().toISOString(),
    range: ordered.length ? { fromSeq: ordered[0]!.seq, toSeq: ordered[ordered.length - 1]!.seq } : null,
    verification:
      "Each record's prevHash equals the previous record's hash. Daily Merkle roots in `anchors` are published externally; compare them with the receipt to prove the ledger was not rewritten after the anchor date.",
    records: ordered,
    anchors,
  };
  downloadText(`evidence-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(bundle, null, 2));
  toast.success(`Exported ${fmtInt(ordered.length)} ledger records`);
}

function readinessReport(records: EvidenceRecord[], anchors: EvidenceAnchor[]) {
  const counts = new Map<EvidenceKind, number>();
  for (const r of records) counts.set(r.kind, (counts.get(r.kind) ?? 0) + 1);
  const seqs = records.map((r) => r.seq);
  const lines = [
    "# Evidence readiness report",
    "",
    `Generated ${new Date().toISOString()}. Readiness mapping, not a certification.`,
    "",
    `Ledger window: ${fmtInt(records.length)} records${seqs.length ? ` (#${Math.min(...seqs)}–#${Math.max(...seqs)})` : ""}.`,
    anchors[0] ? `Latest anchor: ${anchors[0].date} · Merkle root \`${anchors[0].merkleRoot}\` · receipt ${anchors[0].externalReceipt} · ${anchors[0].verified ? "verified" : "not verified"}.` : "No external anchor yet.",
    "",
    "| Framework | Clause | Evidence | Ledger records | Status |",
    "|---|---|---|---|---|",
    ...READINESS_FRAMEWORKS.map((f) => {
      const n = f.kinds.reduce((s, k) => s + (counts.get(k) ?? 0), 0);
      const by = f.kinds.map((k) => `${EVIDENCE_KIND_META[k].label}: ${counts.get(k) ?? 0}`).join(", ");
      return `| ${f.name} | ${f.clause} | ${f.evidence} | ${by} | ${n ? "Evidence available" : "No records yet"} |`;
    }),
    "",
  ];
  downloadText(`readiness-${new Date().toISOString().slice(0, 10)}.md`, lines.join("\n"), "text/markdown");
}

function EvidencePage() {
  const { kind, window = 100 } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const evidence = useQuery(q.evidence(window));
  const anchors = useQuery(q.anchors());
  const agents = useQuery(q.agents());
  const agentNames = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name]) ?? []), [agents.data]);
  const latestAnchor = anchors.data?.[0];

  const counts = useMemo(() => {
    const m = new Map<EvidenceKind, number>();
    for (const r of evidence.data ?? []) m.set(r.kind, (m.get(r.kind) ?? 0) + 1);
    return m;
  }, [evidence.data]);
  const rows = useMemo(() => (kind ? evidence.data?.filter((r) => r.kind === kind) : evidence.data), [evidence.data, kind]);

  const ledgerColumns = useMemo<ColumnDef<EvidenceRecord>[]>(
    () => [
      { id: "seq", header: "Seq", accessorKey: "seq", meta: { align: "right" }, cell: ({ getValue }) => <span className="font-mono text-[12px] text-fg-2">{getValue<number>()}</span> },
      {
        id: "kind",
        header: "Kind",
        accessorKey: "kind",
        cell: ({ row }) => {
          const m = EVIDENCE_KIND_META[row.original.kind];
          return (
            <Badge tone="neutral">
              {m.icon}
              {m.label}
            </Badge>
          );
        },
      },
      { id: "ref", header: "Reference", accessorKey: "refId", cell: ({ row }) => <RefCell record={row.original} /> },
      { id: "summary", header: "Summary", accessorKey: "summary", enableSorting: false, cell: ({ getValue }) => <span className="line-clamp-1 max-w-[320px] text-fg" title={getValue<string>()}>{getValue<string>()}</span> },
      { id: "hash", header: "Hash", accessorKey: "hash", enableSorting: false, cell: ({ getValue }) => <Hash value={getValue<string>()} /> },
      { id: "prevHash", header: "Prev hash", accessorKey: "prevHash", enableSorting: false, cell: ({ getValue }) => <Hash value={getValue<string>()} className="text-fg-3" /> },
      {
        id: "agent",
        header: "Agent",
        accessorFn: (r) => (r.agentId ? (agentNames.get(r.agentId) ?? r.agentId) : ""),
        cell: ({ row }) => (row.original.agentId ? <AgentLink id={row.original.agentId} name={agentNames.get(row.original.agentId) ?? row.original.agentId} className="whitespace-nowrap" /> : <span className="text-fg-3">—</span>),
      },
      { id: "at", header: "Time", accessorKey: "at", cell: ({ getValue }) => <TimeAgo iso={getValue<string>()} className="text-xs" /> },
    ],
    [agentNames],
  );

  return (
    <Page>
      <PageHeader
        title="Evidence"
        description="A hash-chained, externally anchored ledger of every decision and coverage event — the record insurers, auditors and claims adjusters rely on."
        actions={
          <Button variant="outline" disabled={!evidence.data} onClick={() => evidence.data && exportBundle(evidence.data, anchors.data ?? [])}>
            <Download /> Export evidence bundle
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <ChainExplainer records={evidence.data} latestAnchor={latestAnchor} />
          <VerifyChainCard key={window} records={evidence.data} refetch={evidence.refetch} isFetching={evidence.isFetching} />
        </div>

        <Card>
          <CardHeader
            title="Ledger"
            description={
              evidence.data ? (
                <>
                  Newest first · {kind ? `${fmtInt(rows?.length ?? 0)} ${EVIDENCE_KIND_META[kind].label.toLowerCase()} records in` : "showing"} the latest {fmtInt(evidence.data.length)} records
                  {evidence.data.length ? (
                    <>
                      {" "}
                      (<span className="font-mono">#{evidence.data[evidence.data.length - 1]!.seq}</span>–<span className="font-mono">#{evidence.data[0]!.seq}</span>)
                    </>
                  ) : null}
                </>
              ) : (
                "Newest first"
              )
            }
            actions={
              <div className="flex items-center gap-2">
                <Label htmlFor="ledger-kind" className="sr-only">
                  Record kind
                </Label>
                <Select id="ledger-kind" className="w-52" value={kind ?? ""} onChange={(e) => void navigate({ search: (p) => ({ ...p, kind: (e.target.value || undefined) as EvidenceKind | undefined }), replace: true })}>
                  <option value="">All kinds ({fmtInt(evidence.data?.length ?? 0)})</option>
                  {EVIDENCE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {EVIDENCE_KIND_META[k].label} ({fmtInt(counts.get(k) ?? 0)})
                    </option>
                  ))}
                </Select>
                <Label htmlFor="ledger-window" className="sr-only">
                  Window
                </Label>
                <Select
                  id="ledger-window"
                  className="w-36"
                  value={window}
                  onChange={(e) => {
                    const w = Number(e.target.value) as Window;
                    void navigate({ search: (p) => ({ ...p, window: w === 100 ? undefined : w }), replace: true });
                  }}
                >
                  {WINDOWS.map((w) => (
                    <option key={w} value={w}>
                      Latest {w}
                    </option>
                  ))}
                </Select>
              </div>
            }
          />
          {evidence.error ? (
            <div className="p-4">
              <QueryState query={evidence}>{() => null}</QueryState>
            </div>
          ) : (
            <DataTable
              data={rows}
              columns={ledgerColumns}
              loading={evidence.isPending}
              dense
              getRowId={(r) => String(r.seq)}
              className="max-h-[560px]"
              empty={{ icon: <ScrollText />, title: kind ? `No ${EVIDENCE_KIND_META[kind].label.toLowerCase()} records in this window` : "No evidence records yet", description: kind ? "Try a larger window or another kind." : undefined }}
            />
          )}
        </Card>

        <AnchorsCard query={anchors} />
        <ReadinessCard records={evidence.data} anchors={anchors.data ?? []} />
      </div>
    </Page>
  );
}

function RefCell({ record: r }: { record: EvidenceRecord }) {
  const cls = "font-mono text-[12px] text-accent hover:underline";
  const stop = (e: MouseEvent) => e.stopPropagation();
  switch (r.kind) {
    case "issue_transition":
      return (
        <Link to="/issues/$issueId" params={{ issueId: r.refId }} className={cls} onClick={stop}>
          {r.refId}
        </Link>
      );
    case "claim":
      return (
        <Link to="/coverage/claims" className={cls} onClick={stop}>
          {r.refId}
        </Link>
      );
    case "endorsement":
      return (
        <Link to="/coverage/endorsements" className={cls} onClick={stop}>
          {r.refId}
        </Link>
      );
    case "trace_bundle":
      return (
        <Link to="/traces/$traceId" params={{ traceId: r.refId }} className={cls} onClick={stop}>
          {r.refId.slice(0, 12)}
        </Link>
      );
    case "policy_version":
      return r.agentId ? (
        <Link to="/agents/$agentId" params={{ agentId: r.agentId }} className={cls} onClick={stop}>
          {r.refId}
        </Link>
      ) : (
        <span className="font-mono text-[12px] text-fg-2">{r.refId}</span>
      );
    default:
      return <span className="font-mono text-[12px] text-fg-2">{r.refId}</span>;
  }
}

function AnchorsCard({ query }: { query: { data: EvidenceAnchor[] | undefined; isPending: boolean; error: unknown; refetch: () => unknown } }) {
  const columns = useMemo<ColumnDef<EvidenceAnchor>[]>(
    () => [
      { id: "date", header: "Date", accessorKey: "date", cell: ({ getValue }) => <span className="whitespace-nowrap text-fg">{fmtDate(getValue<string>())}</span> },
      { id: "root", header: "Merkle root", accessorKey: "merkleRoot", enableSorting: false, cell: ({ getValue }) => <Hash value={getValue<string>()} n={12} /> },
      { id: "records", header: "Records", accessorKey: "records", meta: { align: "right" }, cell: ({ getValue }) => fmtInt(getValue<number>()) },
      {
        id: "r2Key",
        header: "R2 object (locked)",
        accessorKey: "r2Key",
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="block max-w-[340px] truncate font-mono text-[11.5px] text-fg-2" title={getValue<string>()}>
            {getValue<string>()}
          </span>
        ),
      },
      {
        id: "receipt",
        header: "External receipt",
        accessorKey: "externalReceipt",
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="block max-w-[280px] truncate font-mono text-[11.5px] text-fg-2" title={getValue<string>()}>
            {getValue<string>()}
          </span>
        ),
      },
      {
        id: "verified",
        header: "Verified",
        accessorKey: "verified",
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Tooltip content="Root recomputed from chain heads and RFC 3161 receipt validated">
              <Badge tone="good">
                <CheckCircle2 /> Verified
              </Badge>
            </Tooltip>
          ) : (
            <Badge tone="warning">
              <AlertTriangle /> Pending
            </Badge>
          ),
      },
    ],
    [],
  );
  return (
    <Card>
      <CardHeader title="Daily anchors" description="Merkle roots sealed to bucket-locked R2 and timestamped by an external RFC 3161 authority." />
      {query.error ? (
        <div className="p-4">
          <QueryState query={query}>{() => null}</QueryState>
        </div>
      ) : (
        <DataTable data={query.data} columns={columns} loading={query.isPending} dense getRowId={(a) => a.date} className="max-h-[440px]" empty={{ icon: <Landmark />, title: "No anchors yet", description: "The first Merkle root is anchored at the end of your first day of traffic." }} />
      )}
    </Card>
  );
}

function ReadinessCard({ records, anchors }: { records: EvidenceRecord[] | undefined; anchors: EvidenceAnchor[] }) {
  const present = new Set(records?.map((r) => r.kind));
  return (
    <Card>
      <CardHeader
        title="Readiness report"
        description="How this ledger maps to the frameworks insurers and regulators ask about."
      />
      <CardBody className="p-0">
        <ul className="grid gap-px bg-border md:grid-cols-2">
          {READINESS_FRAMEWORKS.map((f) => (
            <li key={f.id} className="flex items-start justify-between gap-3 bg-surface px-4 py-3 md:last:odd:col-span-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-1.5 text-[13px]">
                  <span className="font-medium text-fg">{f.name}</span>
                  <span className="text-fg-2">{f.clause}</span>
                </div>
                <p className="mt-0.5 text-xs text-fg-3">{f.evidence}</p>
              </div>
              {!records ? null : f.kinds.some((k) => present.has(k)) ? (
                <Badge tone="good" className="mt-0.5 shrink-0">
                  <CheckCircle2 /> Evidence available
                </Badge>
              ) : (
                <Badge tone="neutral" className="mt-0.5 shrink-0">
                  No records yet
                </Badge>
              )}
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
          <p className="flex items-start gap-1.5 text-[11px] leading-snug text-fg-3">
            <FileText className="mt-px size-3.5 shrink-0" aria-hidden />
            Readiness mapping, not a certification. The report cites ledger records and anchor receipts for each control.
          </p>
          <Button size="sm" className="shrink-0" disabled={!records} onClick={() => records && readinessReport(records, anchors)}>
            <FileDown /> Download readiness report
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
